import type {MeshBasicBuilderMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshBasicBuilder';
import {ParamType} from './../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';
import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {ReturnedNodeDataDict} from '../../ExampleUtils';
import {CameraControlsSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';
import {MeshStandardBuilderMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshStandardBuilder';
import {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(50, -50);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, -50);
			spotLight1.p.decay.set(1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.latitude.set(90);
			polarTransform1.p.depth.set(4.5);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['polarTransform1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([lights_nodes['spotLight1'].node]);
		}
		lights.uiData.setPosition(-50, -250);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([-0.30454892650886706, 0.7755729465437615, -0.030681180808110376]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		function create_perspectiveCamera_MAIN(cameras: GeoObjNode) {
			var perspectiveCamera_MAIN = cameras.createNode('perspectiveCamera');
			perspectiveCamera_MAIN.setName('perspectiveCamera_MAIN');
			const perspectiveCamera_MAIN_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_MAIN.uiData.setPosition(300, -50);
			perspectiveCamera_MAIN.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera_MAIN.params.postCreateSpareParams();
			perspectiveCamera_MAIN.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_MAIN, children: perspectiveCamera_MAIN_nodes};
		}
		function create_polarTransform1(cameras: GeoObjNode) {
			var polarTransform1 = cameras.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.5, -0.4]);
			polarTransform1.p.latitude.set(7.2);
			polarTransform1.p.depth.set(5.2);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(cameras: GeoObjNode) {
			var merge1 = cameras.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(150, 350);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		function create_perspectiveCamera_DEBUG(cameras: GeoObjNode) {
			var perspectiveCamera_DEBUG = cameras.createNode('perspectiveCamera');
			perspectiveCamera_DEBUG.setName('perspectiveCamera_DEBUG');
			const perspectiveCamera_DEBUG_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_DEBUG.uiData.setPosition(0, -50);
			perspectiveCamera_DEBUG.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera_DEBUG.params.postCreateSpareParams();
			perspectiveCamera_DEBUG.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_DEBUG, children: perspectiveCamera_DEBUG_nodes};
		}
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['perspectiveCamera_MAIN'] = create_perspectiveCamera_MAIN(cameras);
		cameras_nodes['polarTransform1'] = create_polarTransform1(cameras);
		cameras_nodes['merge1'] = create_merge1(cameras);
		cameras_nodes['perspectiveCamera_DEBUG'] = create_perspectiveCamera_DEBUG(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera_DEBUG'].node);
		cameras_nodes['polarTransform1'].node.setInput(0, cameras_nodes['perspectiveCamera_MAIN'].node);
		cameras_nodes['merge1'].node.setInput(0, cameras_nodes['cameraControls1'].node);
		cameras_nodes['merge1'].node.setInput(1, cameras_nodes['polarTransform1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([cameras_nodes['polarTransform1'].node]);
		}
		cameras.uiData.setPosition(-50, -150);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	function create_room(parentNode: RootManagerNode) {
		var room = parentNode.createNode('geo');
		room.setName('room');
		const room_nodes: ReturnedNodeDataDict = {};
		function create_material1(room: GeoObjNode) {
			var material1 = room.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(0, 300);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandardBuilder1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(room: GeoObjNode) {
			var MAT = room.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandardBuilder1(MAT: MaterialsNetworkSopNode) {
				var meshStandardBuilder1 = MAT.createNode('meshStandardBuilder');
				meshStandardBuilder1.setName('meshStandardBuilder1');
				const meshStandardBuilder1_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var globals1 = meshStandardBuilder1.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-450, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var output1 = meshStandardBuilder1.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(400, 0);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_vec3ToFloat1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var vec3ToFloat1 = meshStandardBuilder1.createNode('vec3ToFloat');
					vec3ToFloat1.setName('vec3ToFloat1');
					const vec3ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat1.uiData.setPosition(-200, 200);
					vec3ToFloat1.params.postCreateSpareParams();
					vec3ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat1, children: vec3ToFloat1_nodes};
				}
				function create_floatToVec2_1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var floatToVec2_1 = meshStandardBuilder1.createNode('floatToVec2');
					floatToVec2_1.setName('floatToVec2_1');
					const floatToVec2_1_nodes: ReturnedNodeDataDict = {};
					floatToVec2_1.uiData.setPosition(-100, 200);
					floatToVec2_1.params.postCreateSpareParams();
					floatToVec2_1.params.runOnSceneLoadHooks();
					return {node: floatToVec2_1, children: floatToVec2_1_nodes};
				}
				function create_checkers1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var checkers1 = meshStandardBuilder1.createNode('checkers');
					checkers1.setName('checkers1');
					const checkers1_nodes: ReturnedNodeDataDict = {};
					checkers1.uiData.setPosition(50, 200);
					checkers1.params.postCreateSpareParams();
					checkers1.params.runOnSceneLoadHooks();
					return {node: checkers1, children: checkers1_nodes};
				}
				function create_mix1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var mix1 = meshStandardBuilder1.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(250, 0);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {spare: true, editable: false});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant1 = meshStandardBuilder1.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(50, -150);
					constant1.p.type.set(4);
					constant1.p.color.set([0.24313725490196078, 0.5098039215686274, 0.8549019607843137]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant2 = meshStandardBuilder1.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(0, 0);
					constant2.p.type.set(4);
					constant2.p.color.set([0.047058823529411764, 0.10196078431372549, 0.17647058823529413]);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				meshStandardBuilder1_nodes['globals1'] = create_globals1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'] = create_output1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['vec3ToFloat1'] = create_vec3ToFloat1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['floatToVec2_1'] = create_floatToVec2_1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['checkers1'] = create_checkers1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['mix1'] = create_mix1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant1'] = create_constant1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant2'] = create_constant2(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'].node.setInput(
					'color',
					meshStandardBuilder1_nodes['mix1'].node,
					'mix'
				);
				meshStandardBuilder1_nodes['vec3ToFloat1'].node.setInput(
					'vec',
					meshStandardBuilder1_nodes['globals1'].node,
					'position'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'x',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'x'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'y',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'z'
				);
				meshStandardBuilder1_nodes['checkers1'].node.setInput(
					'uv',
					meshStandardBuilder1_nodes['floatToVec2_1'].node,
					'vec2'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value0',
					meshStandardBuilder1_nodes['constant1'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value1',
					meshStandardBuilder1_nodes['constant2'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'blend',
					meshStandardBuilder1_nodes['checkers1'].node,
					'checker'
				);
				if (meshStandardBuilder1.childrenController) {
					meshStandardBuilder1.childrenController.selection.set([]);
				}
				meshStandardBuilder1.uiData.setPosition(0, 200);
				meshStandardBuilder1.p.useFog.set(true);
				meshStandardBuilder1.params.postCreateSpareParams();
				meshStandardBuilder1.params.runOnSceneLoadHooks();
				return {node: meshStandardBuilder1, children: meshStandardBuilder1_nodes};
			}
			MAT_nodes['meshStandardBuilder1'] = create_meshStandardBuilder1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([MAT_nodes['meshStandardBuilder1'].node]);
			}
			MAT.uiData.setPosition(-200, 300);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_box2(room: GeoObjNode) {
			var box2 = room.createNode('box');
			box2.setName('box2');
			const box2_nodes: ReturnedNodeDataDict = {};
			box2.uiData.setPosition(0, -300);
			box2.p.center.set([0, 0.5, 0]);
			box2.params.postCreateSpareParams();
			box2.params.runOnSceneLoadHooks();
			return {node: box2, children: box2_nodes};
		}
		function create_transform2(room: GeoObjNode) {
			var transform2 = room.createNode('transform');
			transform2.setName('transform2');
			const transform2_nodes: ReturnedNodeDataDict = {};
			transform2.uiData.setPosition(0, -150);
			transform2.p.scale.set(7);
			transform2.params.postCreateSpareParams();
			transform2.params.runOnSceneLoadHooks();
			return {node: transform2, children: transform2_nodes};
		}
		function create_transform3(room: GeoObjNode) {
			var transform3 = room.createNode('transform');
			transform3.setName('transform3');
			const transform3_nodes: ReturnedNodeDataDict = {};
			transform3.uiData.setPosition(100, 0);
			transform3.p.t.set([0, 0, 1.3288123611722096]);
			transform3.p.scale.set(0.77);
			transform3.params.postCreateSpareParams();
			transform3.params.runOnSceneLoadHooks();
			return {node: transform3, children: transform3_nodes};
		}
		function create_boolean1(room: GeoObjNode) {
			var boolean1 = room.createNode('boolean');
			boolean1.setName('boolean1');
			const boolean1_nodes: ReturnedNodeDataDict = {};
			boolean1.uiData.setPosition(0, 150);
			boolean1.p.operation.set(1);
			boolean1.params.postCreateSpareParams();
			boolean1.params.runOnSceneLoadHooks();
			return {node: boolean1, children: boolean1_nodes};
		}
		function create_transform1(room: GeoObjNode) {
			var transform1 = room.createNode('transform');
			transform1.setName('transform1');
			const transform1_nodes: ReturnedNodeDataDict = {};
			transform1.uiData.setPosition(-100, 0);
			transform1.p.t.set([0, -0.5214257738914512, 0]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return {node: transform1, children: transform1_nodes};
		}
		room_nodes['material1'] = create_material1(room);
		room_nodes['MAT'] = create_MAT(room);
		room_nodes['box2'] = create_box2(room);
		room_nodes['transform2'] = create_transform2(room);
		room_nodes['transform3'] = create_transform3(room);
		room_nodes['boolean1'] = create_boolean1(room);
		room_nodes['transform1'] = create_transform1(room);
		room_nodes['material1'].node.setInput(0, room_nodes['boolean1'].node);
		room_nodes['transform2'].node.setInput(0, room_nodes['box2'].node);
		room_nodes['transform3'].node.setInput(0, room_nodes['transform2'].node);
		room_nodes['boolean1'].node.setInput(0, room_nodes['transform1'].node);
		room_nodes['boolean1'].node.setInput(1, room_nodes['transform3'].node);
		room_nodes['transform1'].node.setInput(0, room_nodes['transform2'].node);
		if (room.childrenController) {
			room.childrenController.selection.set([]);
		}
		room.uiData.setPosition(-50, -450);
		room.flags.display.set(true);
		room.params.postCreateSpareParams();
		room.params.runOnSceneLoadHooks();
		return {node: room, children: room_nodes};
	}
	function create_objects_with_cigar_and_smoke(parentNode: RootManagerNode) {
		var objects_with_cigar_and_smoke = parentNode.createNode('geo');
		objects_with_cigar_and_smoke.setName('objects_with_cigar_and_smoke');
		const objects_with_cigar_and_smoke_nodes: ReturnedNodeDataDict = {};
		function create_fileGLTF1(objects_with_cigar_and_smoke: GeoObjNode) {
			var fileGLTF1 = objects_with_cigar_and_smoke.createNode('fileGLTF');
			fileGLTF1.setName('fileGLTF1');
			const fileGLTF1_nodes: ReturnedNodeDataDict = {};
			fileGLTF1.uiData.setPosition(200, -150);
			fileGLTF1.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/models/resources/threedscans.com/beethoven.glb'
			);
			fileGLTF1.params.postCreateSpareParams();
			fileGLTF1.params.runOnSceneLoadHooks();
			return {node: fileGLTF1, children: fileGLTF1_nodes};
		}
		function create_transform1(objects_with_cigar_and_smoke: GeoObjNode) {
			var transform1 = objects_with_cigar_and_smoke.createNode('transform');
			transform1.setName('transform1');
			const transform1_nodes: ReturnedNodeDataDict = {};
			transform1.uiData.setPosition(200, 100);
			transform1.p.applyOn.set(1);
			transform1.p.t.set([0.4015512522920641, 0, -0.5434346432349156]);
			transform1.p.r.set([0, 106.19354181834774, 0]);
			transform1.p.scale.set(0.55);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return {node: transform1, children: transform1_nodes};
		}
		function create_fileGLTF2(objects_with_cigar_and_smoke: GeoObjNode) {
			var fileGLTF2 = objects_with_cigar_and_smoke.createNode('fileGLTF');
			fileGLTF2.setName('fileGLTF2');
			const fileGLTF2_nodes: ReturnedNodeDataDict = {};
			fileGLTF2.uiData.setPosition(450, -150);
			fileGLTF2.params.postCreateSpareParams();
			fileGLTF2.params.runOnSceneLoadHooks();
			return {node: fileGLTF2, children: fileGLTF2_nodes};
		}
		function create_transform2(objects_with_cigar_and_smoke: GeoObjNode) {
			var transform2 = objects_with_cigar_and_smoke.createNode('transform');
			transform2.setName('transform2');
			const transform2_nodes: ReturnedNodeDataDict = {};
			transform2.uiData.setPosition(450, 100);
			transform2.p.applyOn.set(1);
			transform2.p.t.set([1.5580220523562747, 0, 2.4564630520494397]);
			transform2.p.r.set([0, 151.04595369860274, 0]);
			transform2.p.scale.set(1.54);
			transform2.params.postCreateSpareParams();
			transform2.params.runOnSceneLoadHooks();
			return {node: transform2, children: transform2_nodes};
		}
		function create_fileGLTF3(objects_with_cigar_and_smoke: GeoObjNode) {
			var fileGLTF3 = objects_with_cigar_and_smoke.createNode('fileGLTF');
			fileGLTF3.setName('fileGLTF3');
			const fileGLTF3_nodes: ReturnedNodeDataDict = {};
			fileGLTF3.uiData.setPosition(750, -600);
			fileGLTF3.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/models/resources/threedscans.com/einstein.glb'
			);
			fileGLTF3.params.postCreateSpareParams();
			fileGLTF3.params.runOnSceneLoadHooks();
			return {node: fileGLTF3, children: fileGLTF3_nodes};
		}
		function create_transform3(objects_with_cigar_and_smoke: GeoObjNode) {
			var transform3 = objects_with_cigar_and_smoke.createNode('transform');
			transform3.setName('transform3');
			const transform3_nodes: ReturnedNodeDataDict = {};
			transform3.uiData.setPosition(900, 150);
			transform3.p.applyOn.set(1);
			transform3.p.t.set([-1.6301799087245414, 0, 1.7435302991210109]);
			transform3.p.r.set([0, 76.82703272621258, 0]);
			transform3.p.scale.set(1.77);
			transform3.params.postCreateSpareParams();
			transform3.params.runOnSceneLoadHooks();
			return {node: transform3, children: transform3_nodes};
		}
		function create_merge1(objects_with_cigar_and_smoke: GeoObjNode) {
			var merge1 = objects_with_cigar_and_smoke.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(400, 400);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		function create_hierarchy1(objects_with_cigar_and_smoke: GeoObjNode) {
			var hierarchy1 = objects_with_cigar_and_smoke.createNode('hierarchy');
			hierarchy1.setName('hierarchy1');
			const hierarchy1_nodes: ReturnedNodeDataDict = {};
			hierarchy1.uiData.setPosition(200, 0);
			hierarchy1.p.mode.set(1);
			hierarchy1.params.postCreateSpareParams();
			hierarchy1.params.runOnSceneLoadHooks();
			return {node: hierarchy1, children: hierarchy1_nodes};
		}
		function create_hierarchy2(objects_with_cigar_and_smoke: GeoObjNode) {
			var hierarchy2 = objects_with_cigar_and_smoke.createNode('hierarchy');
			hierarchy2.setName('hierarchy2');
			const hierarchy2_nodes: ReturnedNodeDataDict = {};
			hierarchy2.uiData.setPosition(450, 0);
			hierarchy2.p.mode.set(1);
			hierarchy2.params.postCreateSpareParams();
			hierarchy2.params.runOnSceneLoadHooks();
			return {node: hierarchy2, children: hierarchy2_nodes};
		}
		function create_hierarchy3(objects_with_cigar_and_smoke: GeoObjNode) {
			var hierarchy3 = objects_with_cigar_and_smoke.createNode('hierarchy');
			hierarchy3.setName('hierarchy3');
			const hierarchy3_nodes: ReturnedNodeDataDict = {};
			hierarchy3.uiData.setPosition(750, -450);
			hierarchy3.p.mode.set(1);
			hierarchy3.params.postCreateSpareParams();
			hierarchy3.params.runOnSceneLoadHooks();
			return {node: hierarchy3, children: hierarchy3_nodes};
		}
		function create_material1(objects_with_cigar_and_smoke: GeoObjNode) {
			var material1 = objects_with_cigar_and_smoke.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(400, 700);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandard_ALL_OBJECTS');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(objects_with_cigar_and_smoke: GeoObjNode) {
			var MAT = objects_with_cigar_and_smoke.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandard_ALL_OBJECTS(MAT: MaterialsNetworkSopNode) {
				var meshStandard_ALL_OBJECTS = MAT.createNode('meshStandard');
				meshStandard_ALL_OBJECTS.setName('meshStandard_ALL_OBJECTS');
				const meshStandard_ALL_OBJECTS_nodes: ReturnedNodeDataDict = {};
				meshStandard_ALL_OBJECTS.uiData.setPosition(0, 0);
				meshStandard_ALL_OBJECTS.p.useFog.set(true);
				meshStandard_ALL_OBJECTS.params.postCreateSpareParams();
				meshStandard_ALL_OBJECTS.params.runOnSceneLoadHooks();
				return {node: meshStandard_ALL_OBJECTS, children: meshStandard_ALL_OBJECTS_nodes};
			}
			function create_meshStandardBuilder_CIGAR(MAT: MaterialsNetworkSopNode) {
				var meshStandardBuilder_CIGAR = MAT.createNode('meshStandardBuilder');
				meshStandardBuilder_CIGAR.setName('meshStandardBuilder_CIGAR');
				const meshStandardBuilder_CIGAR_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var globals1 = meshStandardBuilder_CIGAR.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-800, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var output1 = meshStandardBuilder_CIGAR.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(300, -100);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_vec2ToFloat1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var vec2ToFloat1 = meshStandardBuilder_CIGAR.createNode('vec2ToFloat');
					vec2ToFloat1.setName('vec2ToFloat1');
					const vec2ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec2ToFloat1.uiData.setPosition(-500, 50);
					vec2ToFloat1.params.postCreateSpareParams();
					vec2ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec2ToFloat1, children: vec2ToFloat1_nodes};
				}
				function create_smoothstep1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var smoothstep1 = meshStandardBuilder_CIGAR.createNode('smoothstep');
					smoothstep1.setName('smoothstep1');
					const smoothstep1_nodes: ReturnedNodeDataDict = {};
					smoothstep1.uiData.setPosition(-150, 50);
					smoothstep1.addParam(ParamType.FLOAT, 'edge0', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					smoothstep1.params.get('edge0')!.set(0.96);
					smoothstep1.addParam(ParamType.FLOAT, 'edge1', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					smoothstep1.params.get('edge1')!.set(0.85);
					smoothstep1.addParam(ParamType.FLOAT, 'x', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					smoothstep1.params.postCreateSpareParams();
					smoothstep1.params.runOnSceneLoadHooks();
					return {node: smoothstep1, children: smoothstep1_nodes};
				}
				function create_mix1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var mix1 = meshStandardBuilder_CIGAR.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(150, 0);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var constant1 = meshStandardBuilder_CIGAR.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(-200, -350);
					constant1.p.type.set(4);
					constant1.p.color.set([3, 0, 0]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var constant2 = meshStandardBuilder_CIGAR.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(-100, -100);
					constant2.p.type.set(4);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				function create_multAdd1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var multAdd1 = meshStandardBuilder_CIGAR.createNode('multAdd');
					multAdd1.setName('multAdd1');
					const multAdd1_nodes: ReturnedNodeDataDict = {};
					multAdd1.uiData.setPosition(-550, -250);
					multAdd1.addParam(ParamType.FLOAT, 'value', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.addParam(ParamType.FLOAT, 'preAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.addParam(ParamType.FLOAT, 'mult', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.params.get('mult')!.set(2);
					multAdd1.addParam(ParamType.FLOAT, 'postAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.params.postCreateSpareParams();
					multAdd1.params.runOnSceneLoadHooks();
					return {node: multAdd1, children: multAdd1_nodes};
				}
				function create_sin1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var sin1 = meshStandardBuilder_CIGAR.createNode('sin');
					sin1.setName('sin1');
					const sin1_nodes: ReturnedNodeDataDict = {};
					sin1.uiData.setPosition(-400, -250);
					sin1.addParam(ParamType.FLOAT, 'radians', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					sin1.params.postCreateSpareParams();
					sin1.params.runOnSceneLoadHooks();
					return {node: sin1, children: sin1_nodes};
				}
				function create_multScalar1(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var multScalar1 = meshStandardBuilder_CIGAR.createNode('multScalar');
					multScalar1.setName('multScalar1');
					const multScalar1_nodes: ReturnedNodeDataDict = {};
					multScalar1.uiData.setPosition(50, -250);
					multScalar1.addParam(ParamType.VECTOR3, 'value', [1, 1, 1], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multScalar1.addParam(ParamType.FLOAT, 'mult', 1, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multScalar1.params.postCreateSpareParams();
					multScalar1.params.runOnSceneLoadHooks();
					return {node: multScalar1, children: multScalar1_nodes};
				}
				function create_multAdd2(meshStandardBuilder_CIGAR: MeshStandardBuilderMatNode) {
					var multAdd2 = meshStandardBuilder_CIGAR.createNode('multAdd');
					multAdd2.setName('multAdd2');
					const multAdd2_nodes: ReturnedNodeDataDict = {};
					multAdd2.uiData.setPosition(-300, -250);
					multAdd2.addParam(ParamType.FLOAT, 'value', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.addParam(ParamType.FLOAT, 'preAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.addParam(ParamType.FLOAT, 'mult', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.addParam(ParamType.FLOAT, 'postAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.params.get('postAdd')!.set(1.5);
					multAdd2.params.postCreateSpareParams();
					multAdd2.params.runOnSceneLoadHooks();
					return {node: multAdd2, children: multAdd2_nodes};
				}
				meshStandardBuilder_CIGAR_nodes['globals1'] = create_globals1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['output1'] = create_output1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['vec2ToFloat1'] = create_vec2ToFloat1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['smoothstep1'] = create_smoothstep1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['mix1'] = create_mix1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['constant1'] = create_constant1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['constant2'] = create_constant2(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['multAdd1'] = create_multAdd1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['sin1'] = create_sin1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['multScalar1'] = create_multScalar1(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['multAdd2'] = create_multAdd2(meshStandardBuilder_CIGAR);
				meshStandardBuilder_CIGAR_nodes['output1'].node.setInput(
					'emissive',
					meshStandardBuilder_CIGAR_nodes['mix1'].node,
					'mix'
				);
				meshStandardBuilder_CIGAR_nodes['vec2ToFloat1'].node.setInput(
					'vec',
					meshStandardBuilder_CIGAR_nodes['globals1'].node,
					'uv'
				);
				meshStandardBuilder_CIGAR_nodes['smoothstep1'].node.setInput(
					'x',
					meshStandardBuilder_CIGAR_nodes['vec2ToFloat1'].node,
					'y'
				);
				meshStandardBuilder_CIGAR_nodes['mix1'].node.setInput(
					'value0',
					meshStandardBuilder_CIGAR_nodes['multScalar1'].node,
					'val'
				);
				meshStandardBuilder_CIGAR_nodes['mix1'].node.setInput(
					'value1',
					meshStandardBuilder_CIGAR_nodes['constant2'].node,
					'val'
				);
				meshStandardBuilder_CIGAR_nodes['mix1'].node.setInput(
					'blend',
					meshStandardBuilder_CIGAR_nodes['smoothstep1'].node,
					'val'
				);
				meshStandardBuilder_CIGAR_nodes['multAdd1'].node.setInput(
					'value',
					meshStandardBuilder_CIGAR_nodes['globals1'].node,
					'time'
				);
				meshStandardBuilder_CIGAR_nodes['sin1'].node.setInput(
					'radians',
					meshStandardBuilder_CIGAR_nodes['multAdd1'].node,
					'val'
				);
				meshStandardBuilder_CIGAR_nodes['multScalar1'].node.setInput(
					'value',
					meshStandardBuilder_CIGAR_nodes['constant1'].node,
					'val'
				);
				meshStandardBuilder_CIGAR_nodes['multScalar1'].node.setInput(
					'mult',
					meshStandardBuilder_CIGAR_nodes['multAdd2'].node,
					'val'
				);
				meshStandardBuilder_CIGAR_nodes['multAdd2'].node.setInput(
					'value',
					meshStandardBuilder_CIGAR_nodes['sin1'].node,
					'val'
				);
				if (meshStandardBuilder_CIGAR.childrenController) {
					meshStandardBuilder_CIGAR.childrenController.selection.set([]);
				}
				meshStandardBuilder_CIGAR.uiData.setPosition(0, 200);
				meshStandardBuilder_CIGAR.p.emissive.set([1, 1, 1]);
				meshStandardBuilder_CIGAR.p.useFog.set(true);
				meshStandardBuilder_CIGAR.params.postCreateSpareParams();
				meshStandardBuilder_CIGAR.params.runOnSceneLoadHooks();
				return {node: meshStandardBuilder_CIGAR, children: meshStandardBuilder_CIGAR_nodes};
			}
			function create_meshBasicBuilder_LIGHT_SMOKE(MAT: MaterialsNetworkSopNode) {
				var meshBasicBuilder_LIGHT_SMOKE = MAT.createNode('meshBasicBuilder');
				meshBasicBuilder_LIGHT_SMOKE.setName('meshBasicBuilder_LIGHT_SMOKE');
				const meshBasicBuilder_LIGHT_SMOKE_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var globals1 = meshBasicBuilder_LIGHT_SMOKE.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-500, -100);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var output1 = meshBasicBuilder_LIGHT_SMOKE.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(500, -100);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_attribute1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var attribute1 = meshBasicBuilder_LIGHT_SMOKE.createNode('attribute');
					attribute1.setName('attribute1');
					const attribute1_nodes: ReturnedNodeDataDict = {};
					attribute1.uiData.setPosition(-250, 400);
					attribute1.p.name.set('idn');
					attribute1.params.postCreateSpareParams();
					attribute1.params.runOnSceneLoadHooks();
					return {node: attribute1, children: attribute1_nodes};
				}
				function create_multAdd1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var multAdd1 = meshBasicBuilder_LIGHT_SMOKE.createNode('multAdd');
					multAdd1.setName('multAdd1');
					const multAdd1_nodes: ReturnedNodeDataDict = {};
					multAdd1.uiData.setPosition(-250, 200);
					multAdd1.addParam(ParamType.FLOAT, 'value', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.addParam(ParamType.FLOAT, 'preAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.addParam(ParamType.FLOAT, 'mult', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.params.get('mult')!.set(-0.16);
					multAdd1.addParam(ParamType.FLOAT, 'postAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd1.params.postCreateSpareParams();
					multAdd1.params.runOnSceneLoadHooks();
					return {node: multAdd1, children: multAdd1_nodes};
				}
				function create_noise1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var noise1 = meshBasicBuilder_LIGHT_SMOKE.createNode('noise');
					noise1.setName('noise1');
					const noise1_nodes: ReturnedNodeDataDict = {};
					noise1.uiData.setPosition(200, 0);
					noise1.addParam(ParamType.VECTOR3, 'amp', [1, 1, 1], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					noise1.addParam(ParamType.VECTOR3, 'position', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					noise1.addParam(ParamType.VECTOR3, 'freq', [1, 1, 1], {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					noise1.addParam(ParamType.VECTOR3, 'offset', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					noise1.params.postCreateSpareParams();
					noise1.params.runOnSceneLoadHooks();
					return {node: noise1, children: noise1_nodes};
				}
				function create_floatToVec3_1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var floatToVec3_1 = meshBasicBuilder_LIGHT_SMOKE.createNode('floatToVec3');
					floatToVec3_1.setName('floatToVec3_1');
					const floatToVec3_1_nodes: ReturnedNodeDataDict = {};
					floatToVec3_1.uiData.setPosition(0, 400);
					floatToVec3_1.params.postCreateSpareParams();
					floatToVec3_1.params.runOnSceneLoadHooks();
					return {node: floatToVec3_1, children: floatToVec3_1_nodes};
				}
				function create_add1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var add1 = meshBasicBuilder_LIGHT_SMOKE.createNode('add');
					add1.setName('add1');
					const add1_nodes: ReturnedNodeDataDict = {};
					add1.uiData.setPosition(350, -150);
					add1.addParam(ParamType.VECTOR3, 'add0', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					add1.addParam(ParamType.VECTOR3, 'add1', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					add1.addParam(ParamType.VECTOR3, 'add2', [0, 0, 0], {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					add1.params.postCreateSpareParams();
					add1.params.runOnSceneLoadHooks();
					return {node: add1, children: add1_nodes};
				}
				function create_multAdd2(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var multAdd2 = meshBasicBuilder_LIGHT_SMOKE.createNode('multAdd');
					multAdd2.setName('multAdd2');
					const multAdd2_nodes: ReturnedNodeDataDict = {};
					multAdd2.uiData.setPosition(-150, 400);
					multAdd2.addParam(ParamType.FLOAT, 'value', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.addParam(ParamType.FLOAT, 'preAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.addParam(ParamType.FLOAT, 'mult', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.params.get('mult')!.set(0.17);
					multAdd2.addParam(ParamType.FLOAT, 'postAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd2.params.postCreateSpareParams();
					multAdd2.params.runOnSceneLoadHooks();
					return {node: multAdd2, children: multAdd2_nodes};
				}
				function create_floatToVec3_2(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var floatToVec3_2 = meshBasicBuilder_LIGHT_SMOKE.createNode('floatToVec3');
					floatToVec3_2.setName('floatToVec3_2');
					const floatToVec3_2_nodes: ReturnedNodeDataDict = {};
					floatToVec3_2.uiData.setPosition(-150, 200);
					floatToVec3_2.params.postCreateSpareParams();
					floatToVec3_2.params.runOnSceneLoadHooks();
					return {node: floatToVec3_2, children: floatToVec3_2_nodes};
				}
				function create_smoothstep1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var smoothstep1 = meshBasicBuilder_LIGHT_SMOKE.createNode('smoothstep');
					smoothstep1.setName('smoothstep1');
					const smoothstep1_nodes: ReturnedNodeDataDict = {};
					smoothstep1.uiData.setPosition(250, 250);
					smoothstep1.addParam(ParamType.FLOAT, 'edge0', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					smoothstep1.params.get('edge0')!.set(1);
					smoothstep1.addParam(ParamType.FLOAT, 'edge1', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					smoothstep1.params.get('edge1')!.set(0);
					smoothstep1.addParam(ParamType.FLOAT, 'x', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					smoothstep1.params.postCreateSpareParams();
					smoothstep1.params.runOnSceneLoadHooks();
					return {node: smoothstep1, children: smoothstep1_nodes};
				}
				function create_constant1(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var constant1 = meshBasicBuilder_LIGHT_SMOKE.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(300, 0);
					constant1.p.type.set(4);
					constant1.p.color.set([0.07421356837213867, 0.07227185067438519, 0.07227185067438519]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_multAdd3(meshBasicBuilder_LIGHT_SMOKE: MeshBasicBuilderMatNode) {
					var multAdd3 = meshBasicBuilder_LIGHT_SMOKE.createNode('multAdd');
					multAdd3.setName('multAdd3');
					const multAdd3_nodes: ReturnedNodeDataDict = {};
					multAdd3.uiData.setPosition(400, 200);
					multAdd3.addParam(ParamType.FLOAT, 'value', 0, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd3.addParam(ParamType.FLOAT, 'preAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd3.addParam(ParamType.FLOAT, 'mult', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd3.params.get('mult')!.set(0.42);
					multAdd3.addParam(ParamType.FLOAT, 'postAdd', 0, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					multAdd3.params.postCreateSpareParams();
					multAdd3.params.runOnSceneLoadHooks();
					return {node: multAdd3, children: multAdd3_nodes};
				}
				meshBasicBuilder_LIGHT_SMOKE_nodes['globals1'] = create_globals1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['output1'] = create_output1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['attribute1'] = create_attribute1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd1'] = create_multAdd1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['noise1'] = create_noise1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_1'] =
					create_floatToVec3_1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['add1'] = create_add1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd2'] = create_multAdd2(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_2'] =
					create_floatToVec3_2(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['smoothstep1'] = create_smoothstep1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['constant1'] = create_constant1(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd3'] = create_multAdd3(meshBasicBuilder_LIGHT_SMOKE);
				meshBasicBuilder_LIGHT_SMOKE_nodes['output1'].node.setInput(
					'position',
					meshBasicBuilder_LIGHT_SMOKE_nodes['add1'].node,
					'sum'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['output1'].node.setInput(
					'color',
					meshBasicBuilder_LIGHT_SMOKE_nodes['constant1'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['output1'].node.setInput(
					'alpha',
					meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd3'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd1'].node.setInput(
					'value',
					meshBasicBuilder_LIGHT_SMOKE_nodes['globals1'].node,
					'time'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['noise1'].node.setInput(
					'amp',
					meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_1'].node,
					'vec3'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['noise1'].node.setInput(
					'position',
					meshBasicBuilder_LIGHT_SMOKE_nodes['globals1'].node,
					'position'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['noise1'].node.setInput(
					'offset',
					meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_2'].node,
					'vec3'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_1'].node.setInput(
					'x',
					meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd2'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_1'].node.setInput(
					'y',
					meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd2'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_1'].node.setInput(
					'z',
					meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd2'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['add1'].node.setInput(
					'add0',
					meshBasicBuilder_LIGHT_SMOKE_nodes['globals1'].node,
					'position'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['add1'].node.setInput(
					'add1',
					meshBasicBuilder_LIGHT_SMOKE_nodes['noise1'].node,
					'noise'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd2'].node.setInput(
					'value',
					meshBasicBuilder_LIGHT_SMOKE_nodes['attribute1'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['floatToVec3_2'].node.setInput(
					'y',
					meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd1'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['smoothstep1'].node.setInput(
					'x',
					meshBasicBuilder_LIGHT_SMOKE_nodes['attribute1'].node,
					'val'
				);
				meshBasicBuilder_LIGHT_SMOKE_nodes['multAdd3'].node.setInput(
					'value',
					meshBasicBuilder_LIGHT_SMOKE_nodes['smoothstep1'].node,
					'val'
				);
				if (meshBasicBuilder_LIGHT_SMOKE.childrenController) {
					meshBasicBuilder_LIGHT_SMOKE.childrenController.selection.set([]);
				}
				meshBasicBuilder_LIGHT_SMOKE.uiData.setPosition(0, 400);
				meshBasicBuilder_LIGHT_SMOKE.p.transparent.set(true);
				meshBasicBuilder_LIGHT_SMOKE.params.postCreateSpareParams();
				meshBasicBuilder_LIGHT_SMOKE.params.runOnSceneLoadHooks();
				return {node: meshBasicBuilder_LIGHT_SMOKE, children: meshBasicBuilder_LIGHT_SMOKE_nodes};
			}
			MAT_nodes['meshStandard_ALL_OBJECTS'] = create_meshStandard_ALL_OBJECTS(MAT);
			MAT_nodes['meshStandardBuilder_CIGAR'] = create_meshStandardBuilder_CIGAR(MAT);
			MAT_nodes['meshBasicBuilder_LIGHT_SMOKE'] = create_meshBasicBuilder_LIGHT_SMOKE(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(200, 700);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_tube1(objects_with_cigar_and_smoke: GeoObjNode) {
			var tube1 = objects_with_cigar_and_smoke.createNode('tube');
			tube1.setName('tube1');
			const tube1_nodes: ReturnedNodeDataDict = {};
			tube1.uiData.setPosition(1100, -600);
			tube1.p.radius.set(0.019999999999999997);
			tube1.p.height.set(0.3);
			tube1.params.postCreateSpareParams();
			tube1.params.runOnSceneLoadHooks();
			return {node: tube1, children: tube1_nodes};
		}
		function create_merge2(objects_with_cigar_and_smoke: GeoObjNode) {
			var merge2 = objects_with_cigar_and_smoke.createNode('merge');
			merge2.setName('merge2');
			const merge2_nodes: ReturnedNodeDataDict = {};
			merge2.uiData.setPosition(900, -100);
			merge2.params.postCreateSpareParams();
			merge2.params.runOnSceneLoadHooks();
			return {node: merge2, children: merge2_nodes};
		}
		function create_hierarchy4(objects_with_cigar_and_smoke: GeoObjNode) {
			var hierarchy4 = objects_with_cigar_and_smoke.createNode('hierarchy');
			hierarchy4.setName('hierarchy4');
			const hierarchy4_nodes: ReturnedNodeDataDict = {};
			hierarchy4.uiData.setPosition(900, 50);
			hierarchy4.params.postCreateSpareParams();
			hierarchy4.params.runOnSceneLoadHooks();
			return {node: hierarchy4, children: hierarchy4_nodes};
		}
		function create_transform4(objects_with_cigar_and_smoke: GeoObjNode) {
			var transform4 = objects_with_cigar_and_smoke.createNode('transform');
			transform4.setName('transform4');
			const transform4_nodes: ReturnedNodeDataDict = {};
			transform4.uiData.setPosition(1100, -450);
			transform4.p.t.set([-0.017311028939340306, 0.47556467778326994, 0.2503430827236893]);
			transform4.params.postCreateSpareParams();
			transform4.params.runOnSceneLoadHooks();
			return {node: transform4, children: transform4_nodes};
		}
		function create_material2(objects_with_cigar_and_smoke: GeoObjNode) {
			var material2 = objects_with_cigar_and_smoke.createNode('material');
			material2.setName('material2');
			const material2_nodes: ReturnedNodeDataDict = {};
			material2.uiData.setPosition(1100, -350);
			material2.p.material.set('../MAT/meshStandardBuilder_CIGAR');
			material2.params.postCreateSpareParams();
			material2.params.runOnSceneLoadHooks();
			return {node: material2, children: material2_nodes};
		}
		function create_tube2(objects_with_cigar_and_smoke: GeoObjNode) {
			var tube2 = objects_with_cigar_and_smoke.createNode('tube');
			tube2.setName('tube2');
			const tube2_nodes: ReturnedNodeDataDict = {};
			tube2.uiData.setPosition(1850, -750);
			tube2.p.radius.set(0.017000000000000008);
			tube2.p.height.set(0.39);
			tube2.p.center.set([0, 'ch("height")/2', 0]);
			tube2.p.direction.set([0, 1, 0]);
			tube2.params.postCreateSpareParams();
			tube2.params.runOnSceneLoadHooks();
			return {node: tube2, children: tube2_nodes};
		}
		function create_transform5(objects_with_cigar_and_smoke: GeoObjNode) {
			var transform5 = objects_with_cigar_and_smoke.createNode('transform');
			transform5.setName('transform5');
			const transform5_nodes: ReturnedNodeDataDict = {};
			transform5.uiData.setPosition(1600, -400);
			transform5.p.t.set([-0.017082971415020687, 0.4669605788195368, 0.4]);
			transform5.params.postCreateSpareParams();
			transform5.params.runOnSceneLoadHooks();
			return {node: transform5, children: transform5_nodes};
		}
		function create_material3(objects_with_cigar_and_smoke: GeoObjNode) {
			var material3 = objects_with_cigar_and_smoke.createNode('material');
			material3.setName('material3');
			const material3_nodes: ReturnedNodeDataDict = {};
			material3.uiData.setComment(
				'here we create a line, on which we add an `idn` point attribute, which starts at 0 on the first point and ends at 1 on the last point. This attribute will used in the [material](../MAT/meshBasicBuilder_LIGHT_SMOKE) to drive the noise and transparency. '
			);
			material3.uiData.setPosition(1600, -300);
			material3.p.material.set('../MAT/meshBasicBuilder_LIGHT_SMOKE');
			material3.params.postCreateSpareParams();
			material3.params.runOnSceneLoadHooks();
			return {node: material3, children: material3_nodes};
		}
		function create_line1(objects_with_cigar_and_smoke: GeoObjNode) {
			var line1 = objects_with_cigar_and_smoke.createNode('line');
			line1.setName('line1');
			const line1_nodes: ReturnedNodeDataDict = {};
			line1.uiData.setPosition(1600, -750);
			line1.p.length.set(0.39);
			line1.p.pointsCount.set(100);
			line1.params.postCreateSpareParams();
			line1.params.runOnSceneLoadHooks();
			return {node: line1, children: line1_nodes};
		}
		function create_attribId1(objects_with_cigar_and_smoke: GeoObjNode) {
			var attribId1 = objects_with_cigar_and_smoke.createNode('attribId');
			attribId1.setName('attribId1');
			const attribId1_nodes: ReturnedNodeDataDict = {};
			attribId1.uiData.setPosition(1600, -600);
			attribId1.p.id.set(false);
			attribId1.params.postCreateSpareParams();
			attribId1.params.runOnSceneLoadHooks();
			return {node: attribId1, children: attribId1_nodes};
		}
		function create_polywire1(objects_with_cigar_and_smoke: GeoObjNode) {
			var polywire1 = objects_with_cigar_and_smoke.createNode('polywire');
			polywire1.setName('polywire1');
			const polywire1_nodes: ReturnedNodeDataDict = {};
			polywire1.uiData.setPosition(1600, -500);
			polywire1.p.radius.set(0.01);
			polywire1.p.closed.set(false);
			polywire1.params.postCreateSpareParams();
			polywire1.params.runOnSceneLoadHooks();
			return {node: polywire1, children: polywire1_nodes};
		}
		function create_objectProperties1(objects_with_cigar_and_smoke: GeoObjNode) {
			var objectProperties1 = objects_with_cigar_and_smoke.createNode('objectProperties');
			objectProperties1.setName('objectProperties1');
			const objectProperties1_nodes: ReturnedNodeDataDict = {};
			objectProperties1.uiData.setPosition(400, 550);
			objectProperties1.p.group.set('*');
			objectProperties1.p.tcastShadow.set(true);
			objectProperties1.params.postCreateSpareParams();
			objectProperties1.params.runOnSceneLoadHooks();
			return {node: objectProperties1, children: objectProperties1_nodes};
		}
		objects_with_cigar_and_smoke_nodes['fileGLTF1'] = create_fileGLTF1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['transform1'] = create_transform1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['fileGLTF2'] = create_fileGLTF2(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['transform2'] = create_transform2(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['fileGLTF3'] = create_fileGLTF3(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['transform3'] = create_transform3(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['merge1'] = create_merge1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['hierarchy1'] = create_hierarchy1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['hierarchy2'] = create_hierarchy2(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['hierarchy3'] = create_hierarchy3(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['material1'] = create_material1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['MAT'] = create_MAT(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['tube1'] = create_tube1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['merge2'] = create_merge2(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['hierarchy4'] = create_hierarchy4(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['transform4'] = create_transform4(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['material2'] = create_material2(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['tube2'] = create_tube2(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['transform5'] = create_transform5(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['material3'] = create_material3(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['line1'] = create_line1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['attribId1'] = create_attribId1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['polywire1'] = create_polywire1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['objectProperties1'] =
			create_objectProperties1(objects_with_cigar_and_smoke);
		objects_with_cigar_and_smoke_nodes['transform1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['hierarchy1'].node
		);
		objects_with_cigar_and_smoke_nodes['transform2'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['hierarchy2'].node
		);
		objects_with_cigar_and_smoke_nodes['transform3'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['hierarchy4'].node
		);
		objects_with_cigar_and_smoke_nodes['merge1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['transform1'].node
		);
		objects_with_cigar_and_smoke_nodes['merge1'].node.setInput(
			1,
			objects_with_cigar_and_smoke_nodes['transform2'].node
		);
		objects_with_cigar_and_smoke_nodes['merge1'].node.setInput(
			2,
			objects_with_cigar_and_smoke_nodes['transform3'].node
		);
		objects_with_cigar_and_smoke_nodes['hierarchy1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['fileGLTF1'].node
		);
		objects_with_cigar_and_smoke_nodes['hierarchy2'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['fileGLTF2'].node
		);
		objects_with_cigar_and_smoke_nodes['hierarchy3'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['fileGLTF3'].node
		);
		objects_with_cigar_and_smoke_nodes['material1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['objectProperties1'].node
		);
		objects_with_cigar_and_smoke_nodes['merge2'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['hierarchy3'].node
		);
		objects_with_cigar_and_smoke_nodes['merge2'].node.setInput(
			1,
			objects_with_cigar_and_smoke_nodes['material2'].node
		);
		objects_with_cigar_and_smoke_nodes['merge2'].node.setInput(
			2,
			objects_with_cigar_and_smoke_nodes['material3'].node
		);
		objects_with_cigar_and_smoke_nodes['hierarchy4'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['merge2'].node
		);
		objects_with_cigar_and_smoke_nodes['transform4'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['tube1'].node
		);
		objects_with_cigar_and_smoke_nodes['material2'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['transform4'].node
		);
		objects_with_cigar_and_smoke_nodes['transform5'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['polywire1'].node
		);
		objects_with_cigar_and_smoke_nodes['material3'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['transform5'].node
		);
		objects_with_cigar_and_smoke_nodes['attribId1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['line1'].node
		);
		objects_with_cigar_and_smoke_nodes['polywire1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['attribId1'].node
		);
		objects_with_cigar_and_smoke_nodes['objectProperties1'].node.setInput(
			0,
			objects_with_cigar_and_smoke_nodes['merge1'].node
		);
		if (objects_with_cigar_and_smoke.childrenController) {
			objects_with_cigar_and_smoke.childrenController.selection.set([]);
		}
		objects_with_cigar_and_smoke.uiData.setPosition(-50, -350);
		objects_with_cigar_and_smoke.flags.display.set(true);
		objects_with_cigar_and_smoke.params.postCreateSpareParams();
		objects_with_cigar_and_smoke.params.runOnSceneLoadHooks();
		return {node: objects_with_cigar_and_smoke, children: objects_with_cigar_and_smoke_nodes};
	}
	// setup root fog
	root.p.useFog.set(true);
	root.p.fogColor.set([0.85, 0.83, 0.83]);
	root.p.fogDensity.set(0.27);

	//
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);
	scene_root_nodes['room'] = create_room(root);
	scene_root_nodes['objects_with_cigar_and_smoke'] = create_objects_with_cigar_and_smoke(root);
	scene.setFrame(0);
	scene.play();
	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/perspectiveCamera_MAIN',
		focusedNodes: [], // no node is focused here, so that we don't have the transform gizmo appearing
		autoLayout: false,
	};
}
